/**
 * API Client Package
 *
 * Provides a type-safe API client for frontend applications.
 * Supports token management, automatic refresh, error handling, and runtime validation.
 */
// =============================================================================
// API Client Class
// =============================================================================
export class ApiClient {
    config;
    isRefreshing = false;
    refreshPromise = null;
    constructor(config) {
        this.config = config;
    }
    // ---------------------------------------------------------------------------
    // Private Methods
    // ---------------------------------------------------------------------------
    getAuthHeader() {
        const token = this.config.getToken?.();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
    async attemptTokenRefresh() {
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }
        this.isRefreshing = true;
        this.refreshPromise = (async () => {
            try {
                const refreshToken = this.config.getRefreshToken?.();
                if (!refreshToken)
                    return false;
                const response = await fetch(`${this.config.baseUrl}/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken }),
                });
                if (!response.ok)
                    return false;
                const json = (await response.json());
                if (!json.success || !json.data)
                    return false;
                this.config.onTokenRefresh?.(json.data);
                return true;
            }
            catch {
                return false;
            }
            finally {
                this.isRefreshing = false;
                this.refreshPromise = null;
            }
        })();
        return this.refreshPromise;
    }
    /**
     * Public generic request — used by admin-side feature modules to call
     * endpoints that are not yet wrapped with a dedicated typed method.
     * Inherits the same 401 + refresh handling as the internal `request()`.
     */
    async call(method, path, options) {
        return this.request(method, path, options);
    }
    async request(method, path, options) {
        // Resolve URL by concatenating baseUrl + path so a baseUrl with a path
        // (e.g. https://api.example.com/api/v1) is preserved. `new URL(path, base)`
        // with an absolute `path` would silently strip baseUrl's pathname.
        const base = this.config.baseUrl.replace(/\/+$/, '');
        const suffix = path.startsWith('/') ? path : `/${path}`;
        const full = `${base}${suffix}`;
        // base absolute ise (https://...) ikinci arg yok sayilir; base relative
        // ise (/api/v1) mevcut origin'e gore cozulur (nginx same-origin proxy).
        const origin = typeof window !== 'undefined' && window.location ? window.location.origin : undefined;
        const url = /^https?:\/\//.test(full) ? new URL(full) : new URL(full, origin);
        if (options?.params) {
            Object.entries(options.params).forEach(([key, value]) => {
                url.searchParams.set(key, value);
            });
        }
        const headers = {
            'Content-Type': 'application/json',
            ...(options?.requestOptions?.skipAuth ? {} : this.getAuthHeader()),
            ...options?.requestOptions?.headers,
        };
        const fetchConfig = {
            method,
            headers,
            signal: options?.requestOptions?.signal,
            body: options?.body ? JSON.stringify(options.body) : undefined,
        };
        let response = await fetch(url.toString(), fetchConfig);
        // Handle 401 - try token refresh. Skipped for auth endpoints (login /
        // register / refresh pass skipAuth) so a 401 from invalid credentials
        // surfaces to the caller instead of firing onUnauthorized — otherwise a
        // wrong password reloads /login and the error message is lost.
        const skipRefresh = options?.requestOptions?.skipAuth || options?.requestOptions?.skipTokenRefresh;
        if (response.status === 401 && !skipRefresh) {
            const refreshed = await this.attemptTokenRefresh();
            if (refreshed) {
                // Retry once with the new access token.
                const newHeaders = { ...headers, ...this.getAuthHeader() };
                response = await fetch(url.toString(), { ...fetchConfig, headers: newHeaders });
            }
            // Refresh failed, or the retried request is still 401 → session unusable.
            if (response.status === 401) {
                this.config.onUnauthorized?.();
            }
        }
        // 204 No Content has empty body — return synthetic success envelope so
        // callers using ApiResponse shape don't crash on JSON.parse('').
        if (response.status === 204) {
            return {
                success: true,
                data: undefined,
                meta: { timestamp: new Date().toISOString(), requestId: '' },
            };
        }
        // Defensive: some endpoints may return 200 with empty body.
        const text = await response.text();
        if (!text) {
            return {
                success: response.ok,
                data: undefined,
                meta: { timestamp: new Date().toISOString(), requestId: '' },
            };
        }
        return JSON.parse(text);
    }
    // ---------------------------------------------------------------------------
    // Auth Endpoints
    // ---------------------------------------------------------------------------
    async login(data) {
        return this.request('POST', '/auth/login', {
            body: data,
            requestOptions: { skipAuth: true },
        });
    }
    async register(data) {
        return this.request('POST', '/auth/register', {
            body: data,
            requestOptions: { skipAuth: true },
        });
    }
    async refreshToken(refreshToken) {
        return this.request('POST', '/auth/refresh', {
            body: { refreshToken },
            requestOptions: { skipAuth: true, skipTokenRefresh: true },
        });
    }
    async logout(refreshToken) {
        return this.request('POST', '/auth/logout', {
            body: refreshToken ? { refreshToken } : undefined,
        });
    }
    async logoutAll() {
        return this.request('POST', '/auth/logout-all');
    }
    async getCurrentUser() {
        return this.request('GET', '/auth/me');
    }
    async forgotPassword(email) {
        return this.request('POST', '/auth/forgot-password', {
            body: { email },
            requestOptions: { skipAuth: true },
        });
    }
    async resetPassword(token, password) {
        return this.request('POST', '/auth/reset-password', {
            body: { token, password },
            requestOptions: { skipAuth: true },
        });
    }
    async changePassword(currentPassword, newPassword) {
        return this.request('POST', '/auth/change-password', {
            body: { currentPassword, newPassword },
        });
    }
    // ---------------------------------------------------------------------------
    // User Endpoints
    // ---------------------------------------------------------------------------
    async getUsers(params) {
        const queryParams = {};
        if (params?.page)
            queryParams.page = String(params.page);
        if (params?.limit)
            queryParams.limit = String(params.limit);
        if (params?.search)
            queryParams.search = params.search;
        if (params?.sortBy)
            queryParams.sortBy = params.sortBy;
        if (params?.sortOrder)
            queryParams.sortOrder = params.sortOrder;
        return this.request('GET', '/users', { params: queryParams });
    }
    async getUser(id) {
        return this.request('GET', `/users/${id}`);
    }
    async updateUser(id, data) {
        return this.request('PATCH', `/users/${id}`, { body: data });
    }
    async deleteUser(id) {
        return this.request('DELETE', `/users/${id}`);
    }
}
// =============================================================================
// Factory Function
// =============================================================================
export function createApiClient(config) {
    return new ApiClient(config);
}
// Export validation utilities
export * from './validation';
//# sourceMappingURL=index.js.map